import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import EntityFormModal from '@sharedCR/common/EntityFormModal';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import { PENDING_CHANGES_SUBTAB } from 'app/pages/loanApplication/LoanHeaders/RedesignedLoanInfoHeader/PendingChangesDrawer/PendingChangesDrawer.constants';
import { CUSTOM_FIELDS_TARGET } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import { filterDisplayableEnabledDefinitionsForTarget } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.utils';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useReleaseMyFieldLocks from 'hooks/useReleaseMyFieldLocks';
import useRevertFieldValues from 'hooks/useRevertFieldValues';

import { getMasterPermissionsToPage, selectMasterCompanyId } from 'store/auth/selectors';
import { loadCustomFieldsAction } from 'store/customFieldDefinitions/actions';
import {
  selectCommonInfoLoanApplication,
  selectHiddenFieldIds,
  selectHiddenPageIds,
  selectIncompleteLinkedNav,
  selectInvalidTabs,
  selectIsChattelLoanFromPropertyAndLoanSummary,
  selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  selectIsLoanLenderEngineType,
  selectIsLoanOptimalBlueOrSifterEngineType,
  selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary,
  selectIsLoanPurposePurchaseFromPropertyAndLoanSummary,
  selectIsPropertyAndLoanSummaryEmpty,
  selectIsUnsecuredFromPropertyAndLoanSummary,
  selectLoanContactsView,
} from 'store/loanApplications/selectors';
import { clearLocalEditedFieldsAction } from 'store/presence/actions';
import { selectLocalEditedFieldPaths } from 'store/presence/selectors';
import { selectCustomFields } from 'store/system/selectors/customFieldDefinitions.selectors';

import Content from './Content';
import { Title } from './Content/AdditionalComponents';
import {
  ENTITIES_WITH_DYNAMIC_SECTIONS,
  ENTITY_TYPE_HREF_MAP,
  GET_ENTITY_SECTION_HREFS_LIST_FUNCTION_MAP,
  getCustomFieldsSectionOverrides,
  getHeadTabs,
  getNavigationTabs,
  getSectionToRedirectByEntityType,
} from './EditModal.constants';
import useDynamicRecords from './hooks/useDynamicRecords';

const EditModal = ({ isLoading, entityTypes }) => {
  const dispatch = useDispatch();

  const { applicationId, entityId: section, subtab, tab } = useParams();
  const history = useHistory();

  const revertFieldValues = useRevertFieldValues();
  const releaseMyFieldLocks = useReleaseMyFieldLocks();
  const localEditedFieldPaths = useSelector(selectLocalEditedFieldPaths);
  const isExternalLoanLockEnabled = useFeatureToggle(FeatureFlag.EnableExternalLoanLock);
  const isMortgageInsuranceFieldsEnabled = useFeatureToggle(
    FeatureFlag.EnableMortgageInsuranceFields,
  );
  const isOBEngineType = useSelector(selectIsLoanOptimalBlueOrSifterEngineType);
  const isLenderEngineType = useSelector(selectIsLoanLenderEngineType);

  const shouldShowOBCustomFieldsSection = isOBEngineType && isExternalLoanLockEnabled;
  const shouldShowMortgageInsuranceSection = isLenderEngineType && isMortgageInsuranceFieldsEnabled;

  const masterCompanyId = useSelector(selectMasterCompanyId);
  const allCustomFieldDefinitions = useSelector(selectCustomFields);

  const applicationCustomFieldDefinitions = useMemo(
    () =>
      filterDisplayableEnabledDefinitionsForTarget(
        allCustomFieldDefinitions,
        CUSTOM_FIELDS_TARGET.LOAN,
      ),
    [allCustomFieldDefinitions],
  );

  const shouldShowApplicationCustomFieldsSection = !!applicationCustomFieldDefinitions.length;

  const propertyCustomFieldDefinitions = useMemo(
    () =>
      filterDisplayableEnabledDefinitionsForTarget(
        allCustomFieldDefinitions,
        CUSTOM_FIELDS_TARGET.PROPERTY,
      ),
    [allCustomFieldDefinitions],
  );

  const shouldShowPropertyCustomFieldsSection = !!propertyCustomFieldDefinitions.length;

  useEffectSafeStrictMode(() => {
    if (!masterCompanyId) {
      return;
    }

    dispatch(
      loadCustomFieldsAction({
        companyId: masterCompanyId,
        targets: [
          CUSTOM_FIELDS_TARGET.LOAN,
          CUSTOM_FIELDS_TARGET.PROPERTY,
          CUSTOM_FIELDS_TARGET.SUBJECT_PROPERTY,
        ],
        enabledOnly: true,
        merge: true,
      }),
    );
  }, [dispatch, masterCompanyId]);

  const hiddenPageIds = useSelector(selectHiddenPageIds);
  const hiddenFieldIds = useSelector(selectHiddenFieldIds);
  const navNeededByAreaId = useSelector(selectIncompleteLinkedNav());
  const invalidTabs = useSelector(selectInvalidTabs);
  const { loanNumber } = useSelector(selectCommonInfoLoanApplication);
  const isPurchase = useSelector(selectIsLoanPurposePurchaseFromPropertyAndLoanSummary);
  const isAdditionalLoan = useSelector(selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary);
  const isHelocPurposePurchase = useSelector(
    selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  );
  const isUnsecured = useSelector(selectIsUnsecuredFromPropertyAndLoanSummary);
  const isChattel = useSelector(selectIsChattelLoanFromPropertyAndLoanSummary);
  const isPropertyAndLoanSummaryEmpty = useSelector(selectIsPropertyAndLoanSummaryEmpty);
  const { isViewAllowed: shouldShowLoanRolesSection } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_LOAN_ROLE_ASSIGNEMENT),
  );
  const { isViewAllowed: shouldShowLoanTagsSection } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_LOAN_TAG_ASSIGNEMENT),
  );
  const { isViewAllowed: shouldShowProfitAndLossSection } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_PROFIT_AND_LOSS),
  );
  const { isViewAllowed: shouldShowTransactionDetailsSection } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_TRANSACTION_DETAILS),
  );
  const { isViewAllowed: isAppraisalSelectionViewAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_APPRAISAL_SELECTION),
  );
  const contactsView = useSelector(selectLoanContactsView);

  const isCorrectPath = section && entityTypes.includes(subtab);
  const isDynamicSectionsEntity = ENTITIES_WITH_DYNAMIC_SECTIONS.includes(subtab);
  const isPendingChangesDrawerOpen = subtab === PENDING_CHANGES_SUBTAB;

  const [isNestedEntityEditActive, setIsNestedEntityEditActive] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});

  const {
    isManageEntityAllowed,
    isNewRecordFlow,
    newRecords,
    newAddedInitialRecords,
    deletedRecords,
    deletedInitialRecords,
    onDeleteRecord,
    onRestoreRecord,
    onClearDynamicRecordsState,
  } = useDynamicRecords();

  const onClose = useCallback(() => {
    if (localEditedFieldPaths.length) {
      releaseMyFieldLocks();
      revertFieldValues(localEditedFieldPaths);
      dispatch(clearLocalEditedFieldsAction());
    }

    // Already on the plain tab route (no drawer open): pushing it again would only add a history entry.
    if (subtab) {
      history.push(LoansPaths.applicationManagement.generatePath({ applicationId, tab }));
    }
  }, [
    history,
    applicationId,
    tab,
    subtab,
    localEditedFieldPaths,
    revertFieldValues,
    releaseMyFieldLocks,
    dispatch,
  ]);

  const additionalProps = useMemo(
    () => ({
      setPendingChanges,
      newRecords,
      newAddedInitialRecords,
      deletedRecords,
      deletedInitialRecords,
    }),
    [setPendingChanges, newRecords, newAddedInitialRecords, deletedRecords, deletedInitialRecords],
  );

  useEffect(() => {
    if (isCorrectPath || isPendingChangesDrawerOpen) {
      return;
    }

    onClose();
    setIsNestedEntityEditActive(false);
    onClearDynamicRecordsState();
    setPendingChanges({});
  }, [isCorrectPath, isPendingChangesDrawerOpen, onClose, onClearDynamicRecordsState]);

  const navigationTabs = useMemo(
    () =>
      getNavigationTabs({
        pendingChanges,
        applicationId,
        tab,
        subtab,
        section,
        history,
        isPurchase,
        isAdditionalLoan,
        isNestedEntityEditActive,
        setIsNestedEntityEditActive,
        isHelocPurposePurchase,
        isUnsecured,
        isChattel,
        shouldShowLoanRolesSection,
        dispatch,
        shouldShowLoanTagsSection,
        shouldShowProfitAndLossSection,
        shouldShowTransactionDetailsSection,
        shouldShowOBCustomFieldsSection,
        shouldShowMortgageInsuranceSection,
        shouldShowApplicationCustomFieldsSection,
        shouldShowPropertyCustomFieldsSection,
        isAppraisalSelectionViewAllowed,
        invalidTabs,
        contactsView,
        isManageEntityAllowed,
        newRecords,
        deletedRecords,
        deletedInitialRecords,
        onDeleteRecord,
        onRestoreRecord,
        hiddenPageIds,
        hiddenFieldIds,
        navNeededByAreaId,
      }),
    [
      pendingChanges,
      applicationId,
      tab,
      subtab,
      section,
      history,
      isPurchase,
      isAdditionalLoan,
      isHelocPurposePurchase,
      isUnsecured,
      isChattel,
      shouldShowLoanRolesSection,
      shouldShowLoanTagsSection,
      isNestedEntityEditActive,
      setIsNestedEntityEditActive,
      shouldShowProfitAndLossSection,
      shouldShowTransactionDetailsSection,
      shouldShowOBCustomFieldsSection,
      shouldShowMortgageInsuranceSection,
      shouldShowApplicationCustomFieldsSection,
      shouldShowPropertyCustomFieldsSection,
      isAppraisalSelectionViewAllowed,
      dispatch,
      invalidTabs,
      contactsView,
      isManageEntityAllowed,
      newRecords,
      deletedRecords,
      deletedInitialRecords,
      onDeleteRecord,
      onRestoreRecord,
      hiddenPageIds,
      hiddenFieldIds,
      navNeededByAreaId,
    ],
  );

  const headTabs = useMemo(
    () =>
      getHeadTabs({
        pendingChangesCount: _.sumBy(navigationTabs, 'pendingChangesCount'),
        loanNumber,
      }),
    [loanNumber, navigationTabs],
  );

  useEffect(() => {
    // A dynamic-sections entity (contacts) validates its section against its own records, which
    // the Contacts tab loads without the Property & Loan summary.
    if (
      isLoading ||
      !isCorrectPath ||
      (isPropertyAndLoanSummaryEmpty && !isDynamicSectionsEntity)
    ) {
      return;
    }

    const { isPropertyCustomFieldsActive, isApplicationCustomFieldsActive } =
      getCustomFieldsSectionOverrides({ subtab, section });

    if (
      isNewRecordFlow ||
      GET_ENTITY_SECTION_HREFS_LIST_FUNCTION_MAP[subtab]({
        isPurchase,
        isAdditionalLoan,
        isHelocPurposePurchase,
        isUnsecured,
        isChattel,
        shouldShowLoanRolesSection,
        shouldShowLoanTagsSection,
        shouldShowProfitAndLossSection,
        shouldShowTransactionDetailsSection,
        shouldShowOBCustomFieldsSection,
        shouldShowMortgageInsuranceSection,
        shouldShowApplicationCustomFieldsSection:
          shouldShowApplicationCustomFieldsSection || isApplicationCustomFieldsActive,
        shouldShowPropertyCustomFieldsSection:
          shouldShowPropertyCustomFieldsSection || isPropertyCustomFieldsActive,
        isAppraisalSelectionViewAllowed,
        contactsView,
      }).includes(section)
    ) {
      return;
    }

    history.push(
      LoansPaths.applicationManagement.generatePath({
        applicationId,
        section: getSectionToRedirectByEntityType({
          entityType: subtab,
          isChattel,
          contactsView,
          deletedRecords,
          deletedInitialRecords,
        }),
        tab,
        subtab,
      }),
    );
  }, [
    shouldShowOBCustomFieldsSection,
    shouldShowMortgageInsuranceSection,
    shouldShowApplicationCustomFieldsSection,
    shouldShowPropertyCustomFieldsSection,
    applicationId,
    isPropertyAndLoanSummaryEmpty,
    isDynamicSectionsEntity,
    section,
    tab,
    subtab,
    history,
    isCorrectPath,
    isPurchase,
    isAdditionalLoan,
    isHelocPurposePurchase,
    isUnsecured,
    isChattel,
    shouldShowLoanRolesSection,
    shouldShowLoanTagsSection,
    shouldShowProfitAndLossSection,
    shouldShowTransactionDetailsSection,
    isAppraisalSelectionViewAllowed,
    contactsView,
    isLoading,
    isNewRecordFlow,
    deletedRecords,
    deletedInitialRecords,
  ]);

  return (
    <EntityFormModal
      isData
      Content={Content}
      additionalProps={additionalProps}
      headTabs={headTabs}
      headValue={headTabs[0].value}
      isLoading={isLoading}
      isOpen={!!isCorrectPath}
      navigationTabs={navigationTabs}
      navigationValue={section}
      setIsNestedEntityEditActive={setIsNestedEntityEditActive}
      title={<Title section={section} subtab={subtab} />}
      onClose={onClose}
    />
  );
};

EditModal.propTypes = {
  ...WithLoadingPropTypes,
  entityTypes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(ENTITY_TYPE_HREF_MAP))).isRequired,
};

export default memo(EditModal);
